import React, { useMemo } from "react";
import { FilterShowcase } from "../../../../../../../../Components/FilterShowcase/filterShowcase.index";
import { UserContext, UserPayload } from "../../../../../../../../Context/userContext";
import { useEscalationInfo } from "../../../../../../../../Context/escalationContext";
import { EscalationFilters } from "../../../../../../../../Models/escalatedPageModels";
import { useEscalatedDataFetch } from "../../../../../../escalatedPage.hooks";
import { CREATED_AT_KEY, IS_MAIL_SENT_KEY } from "../../../../../../escalatedPage.constants";
import { buildPayload } from "../../../../escalatedPageHeader.utils";
import { buildEscalationFilterChips } from "./escalationFilterShowcase.utils";

interface EscalationFilterShowcaseProps {
    appliedFilters: EscalationFilters;
    setAppliedFilters: (filters: EscalationFilters) => void;
}

export const EscalationFilterShowcase = ({
    appliedFilters,
    setAppliedFilters,
}: EscalationFilterShowcaseProps) => {
    const { fetchEscalatedData } = useEscalatedDataFetch();
    const {
        escalationList,
        escalationStatusList,
        createdByList,
        searchValue,
        paginationDetail,
        setPaginationDetail,
        sortInfo,
    } = useEscalationInfo();
    const { userDetail } = React.useContext(UserContext) as UserPayload;
    const devId = userDetail.value.devId;

    const chips = useMemo(
        () =>
            buildEscalationFilterChips({
                filters: appliedFilters,
                devId,
                escalationList: escalationList ?? [],
                statusList: escalationStatusList ?? [],
                createdByList,
            }),
        [appliedFilters, escalationList, escalationStatusList, createdByList, devId]
    );

    const applyFilters = async (newFilters: EscalationFilters) => {
        const payload = buildPayload({
            filters: newFilters,
            pageSize: paginationDetail.rowsPerPage,
            pageIndex: 1,
            searchParam: searchValue,
            sort: sortInfo,
        });
        setPaginationDetail((prevState) => ({
            ...prevState,
            page: 0,
        }));
        const response = await fetchEscalatedData(payload);
        if (response) {
            setAppliedFilters(newFilters);
        }
    };

    const handleClearAllFilters = async () => {
        await applyFilters({
            status: [],
            escalationType: [],
            isMailSent: false,
            createdAt: undefined,
            customStartDate: null,
            customEndDate: null,
            createdBy: [],
        });
    };

    const handleRemove = async (key: keyof EscalationFilters) => {
        const newFilters: EscalationFilters = { ...appliedFilters };
        if (key === IS_MAIL_SENT_KEY) {
            newFilters.isMailSent = false;
        } else if (key === CREATED_AT_KEY) {
            newFilters.createdAt = undefined;
            newFilters.customStartDate = null;
            newFilters.customEndDate = null;
        } else {
            (newFilters as unknown as Record<string, string[] | undefined>)[key] = [];
        }
        await applyFilters(newFilters);
    };

    return (
        <FilterShowcase
            chips={chips}
            onRemove={handleRemove}
            onClearAll={handleClearAllFilters}
        />
    );
};
